import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  CheckCircle2,
  FileText,
  Link as LinkIcon,
  Trash2,
  Upload,
} from "lucide-react";
import { useTRPC, trpcClient } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileUploadInput } from "@/components/file-upload/file-upload-input";
import type { DeclaredUploadFile } from "@/ajax/upload/declare-upload-file";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label as FormLabel } from "@/components/ui/label";
import { HiringProcessItemStatus } from "@api/shared-runtime/enums";
import type { RouterOutputs } from "@/trpc";
import { toast } from "sonner";
import { formatBytes } from "./helpers";

type HiringProcessInstanceItem =
  RouterOutputs["hiringProcess"]["listCandidates"][number]["items"][number];

/**
 * A single row showing one uploaded candidate file. Combines the
 * provider-facing label (editable), the internal fileName + size, and a
 * remove button — so the org sees and manages everything in one place.
 * Label saves on blur (when the value actually changed).
 */
function CandidateFileRow({
  attachmentId,
  initialLabel,
  fileName,
  fileSizeBytes,
  onRenameLabel,
  onRemove,
  renaming,
  removing,
}: {
  attachmentId: string;
  initialLabel: string;
  fileName: string;
  fileSizeBytes: number;
  onRenameLabel: (label: string) => void;
  onRemove: () => void;
  renaming: boolean;
  removing: boolean;
}) {
  const [value, setValue] = useState(initialLabel);
  // Keep the input in sync if the backend value changes (e.g., successful
  // rename races with a query invalidation).
  React.useEffect(() => {
    setValue(initialLabel);
  }, [initialLabel]);

  const commitIfChanged = () => {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      setValue(initialLabel);
      return;
    }
    if (trimmed !== initialLabel) {
      onRenameLabel(trimmed);
    }
  };

  return (
    <div className="flex items-center gap-2 px-2.5 py-2 rounded-md bg-white border border-gray-100">
      <FileText size={16} className="text-blue-500 shrink-0" />
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <Input
          id={`label-${attachmentId}`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commitIfChanged}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
          className="h-7 py-0.5 text-sm border-transparent hover:border-gray-200 focus:border-blue-300 px-1.5"
          placeholder="Label shown to candidate"
          disabled={renaming || removing}
        />
        <span
          className="text-[11px] text-gray-400 truncate px-1.5"
          title={`Original file: ${fileName}`}
        >
          {fileName} · {formatBytes(fileSizeBytes)}
        </span>
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={removing}
        className="shrink-0 p-1 text-gray-300 hover:text-red-500 disabled:opacity-50"
        aria-label="Remove file"
        title="Remove"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

export function CandidateContentPanel({
  item,
  candidateUserId,
  jobPostId,
  organizationBusinessUnitId,
  onStatusChange,
  isUpdating,
}: {
  item: HiringProcessInstanceItem;
  candidateUserId: string;
  jobPostId: string;
  organizationBusinessUnitId: string;
  onStatusChange: (id: string, status: HiringProcessItemStatus) => void;
  isUpdating: boolean;
}) {
  const trpc = useTRPC();
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [newLinkLabel, setNewLinkLabel] = useState("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  const invalidate = async () => {
    await queryClient.invalidateQueries(
      trpc.hiringProcess.listCandidates.queryOptions({
        organizationBusinessUnitId,
        jobPostId,
      }),
    );
  };

  const addMutation = useMutation(
    trpc.hiringProcess.addCandidateItemContent.mutationOptions({
      onError: (err) => toast.error(err.message || "Failed to add content"),
    }),
  );
  const removeFileMutation = useMutation(
    trpc.hiringProcess.removeCandidateItemFile.mutationOptions({
      onSuccess: invalidate,
      onError: (err) => toast.error(err.message || "Failed to remove file"),
    }),
  );
  const renameFileMutation = useMutation(
    trpc.hiringProcess.renameCandidateItemFile.mutationOptions({
      onSuccess: invalidate,
      onError: (err) => toast.error(err.message || "Failed to rename file"),
    }),
  );
  const removeLinkMutation = useMutation(
    trpc.hiringProcess.removeCandidateItemLink.mutationOptions({
      onSuccess: invalidate,
      onError: (err) => toast.error(err.message || "Failed to remove link"),
    }),
  );

  /**
   * Creates a file_upload + attachment row server-side (inside a progress row)
   * and returns the storage info so `FileUploadInput` can run the TUS upload.
   *
   * `label` defaults to the filename — the provider-facing name. The org can
   * rename it after upload via the rename UI below. The provider never sees
   * the underlying filename; only this label reaches them.
   */
  const createUpload = async (fileMeta: DeclaredUploadFile) => {
    const result = await addMutation.mutateAsync({
      organizationBusinessUnitId,
      jobPostId,
      userId: candidateUserId,
      hiringProcessItemId: item.hiringProcessItemId,
      newFiles: [{ ...fileMeta, label: fileMeta.fileName }],
    });
    const pending = result.pendingUploads[0];
    return {
      id: pending.fileUploadId,
      storagePath: pending.storagePath,
      bucketName: pending.bucketName,
      uploadToken: pending.uploadToken,
    };
  };

  const handleFileUploaded = async (uploaded: { fileUploadId: string }) => {
    await trpcClient.fileAttachment.confirmUploads.mutate({
      fileUploadIds: [uploaded.fileUploadId],
    });
    await invalidate();
  };

  const handleFileRemoved = (fileUploadId: string) => {
    // The unified attachment table can hold a library-version row (no
    // `fileUpload`) in addition to direct uploads, but this panel only
    // renders for org-assigned items which always go through the direct
    // upload path. Guard against null defensively to satisfy the
    // post-unification type.
    const fa = item.candidateFiles?.find(
      (cf) => cf.fileUpload?.id === fileUploadId,
    );
    if (!fa) {
      return;
    }
    removeFileMutation.mutate({
      organizationBusinessUnitId,
      fileAttachmentId: fa.id,
    });
  };

  const handleAddLink = async () => {
    if (!newLinkUrl.trim()) {
      return;
    }
    await addMutation.mutateAsync({
      organizationBusinessUnitId,
      jobPostId,
      userId: candidateUserId,
      hiringProcessItemId: item.hiringProcessItemId,
      newLinks: [
        {
          url: newLinkUrl.trim(),
          label: newLinkLabel.trim() || null,
        },
      ],
    });
    setNewLinkUrl("");
    setNewLinkLabel("");
    setLinkDialogOpen(false);
    await invalidate();
  };

  const handleUploadFileUploaded = async (uploaded: {
    fileUploadId: string;
  }) => {
    await handleFileUploaded(uploaded);
    // Let the user upload multiple files before closing — don't auto-dismiss.
  };

  const hasContent =
    (item.candidateFiles?.length ?? 0) > 0 ||
    (item.candidateLinks?.length ?? 0) > 0;
  const isCompleted = item.status === HiringProcessItemStatus.Completed;

  return (
    // Indented so this reads as the child content of the item row above.
    // The row is: px-3 (12) + w-5 (20) + gap-3 (12) + status-icon (16) +
    // gap-3 (12) = 72px to the text content's left edge — `pl-[72px]`
    // aligns the panel content with that text.
    <div className="pl-[72px] pr-3 pb-3 pt-2 space-y-3 bg-white">
      <div className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
        <Upload size={12} /> Add a file or link for this candidate
      </div>

      {/*
       * Committed files list. The drop zone lives in a dialog (triggered
       * below) so it doesn't take space in-line.
       */}
      {(item.candidateFiles?.length ?? 0) > 0 && (
        <div className="space-y-1.5">
          {item.candidateFiles
            ?.filter(
              (
                fa,
              ): fa is typeof fa & {
                fileUpload: NonNullable<typeof fa.fileUpload>;
              } => fa.fileUpload != null,
            )
            .map((fa) => (
              <CandidateFileRow
                key={fa.id}
                attachmentId={fa.id}
                initialLabel={fa.label}
                fileName={fa.fileUpload.fileName}
                fileSizeBytes={fa.fileUpload.fileSizeBytes}
                renaming={renameFileMutation.isPending}
                removing={removeFileMutation.isPending}
                onRenameLabel={(label) =>
                  renameFileMutation.mutate({
                    organizationBusinessUnitId,
                    fileAttachmentId: fa.id,
                    label,
                  })
                }
                onRemove={() =>
                  removeFileMutation.mutate({
                    organizationBusinessUnitId,
                    fileAttachmentId: fa.id,
                  })
                }
              />
            ))}
        </div>
      )}

      {(item.candidateLinks?.length ?? 0) > 0 && (
        <div className="space-y-1">
          {item.candidateLinks?.map((link) => (
            <div
              key={link.id}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-100 text-sm"
            >
              <LinkIcon size={14} className="text-gray-500 shrink-0" />
              <span className="flex-1 min-w-0 truncate text-gray-700">
                {link.label ? `${link.label} — ` : ""}
                {link.url}
              </span>
              <button
                type="button"
                className="text-gray-300 hover:text-red-500 shrink-0 p-0.5"
                aria-label="Remove link"
                onClick={() =>
                  removeLinkMutation.mutate({
                    organizationBusinessUnitId,
                    linkId: link.id,
                  })
                }
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 px-3 text-xs"
          onClick={() => setUploadDialogOpen(true)}
        >
          <Upload size={12} className="mr-1.5" /> Upload file
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 px-3 text-xs"
          onClick={() => setLinkDialogOpen(true)}
        >
          <LinkIcon size={12} className="mr-1.5" /> Add link
        </Button>
      </div>

      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-xl gap-0">
          <DialogHeader className="space-y-1.5 pb-5">
            <DialogTitle>Upload file for this candidate</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Drag and drop or browse. You can upload multiple files — each one
              will appear in the list once it finishes.
            </DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <FileUploadInput
              files={[]}
              onFileUploaded={handleUploadFileUploaded}
              onFileRemoved={handleFileRemoved}
              createUpload={createUpload}
            />
          </div>
          <DialogFooter className="pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setUploadDialogOpen(false)}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={linkDialogOpen}
        onOpenChange={(open) => {
          setLinkDialogOpen(open);
          if (!open) {
            setNewLinkUrl("");
            setNewLinkLabel("");
          }
        }}
      >
        <DialogContent className="gap-0">
          <DialogHeader className="space-y-1.5 pb-5">
            <DialogTitle>Add a link</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              The link appears on the candidate's checklist. Add a label to make
              it easier to recognize.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <FormLabel htmlFor="new-link-label" className="text-sm">
                  Label
                </FormLabel>
                <span className="text-xs text-muted-foreground">Optional</span>
              </div>
              <Input
                id="new-link-label"
                placeholder="e.g. Employee handbook"
                value={newLinkLabel}
                onChange={(e) => setNewLinkLabel(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <FormLabel htmlFor="new-link-url" className="text-sm">
                URL
              </FormLabel>
              <Input
                id="new-link-url"
                type="url"
                placeholder="https://..."
                value={newLinkUrl}
                onChange={(e) => setNewLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    newLinkUrl.trim() &&
                    !addMutation.isPending
                  ) {
                    e.preventDefault();
                    handleAddLink();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter className="pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setLinkDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddLink}
              disabled={!newLinkUrl.trim() || addMutation.isPending}
            >
              Add link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        {isCompleted ? (
          <>
            <span className="text-xs text-green-700 flex items-center gap-1.5 font-medium">
              <CheckCircle2 size={14} /> Step completed
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2.5 text-xs"
              disabled={isUpdating}
              onClick={() =>
                onStatusChange(item.id, HiringProcessItemStatus.InProgress)
              }
            >
              Reopen
            </Button>
          </>
        ) : (
          <>
            <span className="text-xs text-gray-500">
              {hasContent
                ? "Finished adding files or links? Mark this step complete."
                : "Add at least one file or link, then mark complete."}
            </span>
            <Button
              type="button"
              size="sm"
              className="h-7 px-3 text-xs"
              disabled={!hasContent || isUpdating}
              onClick={() =>
                onStatusChange(item.id, HiringProcessItemStatus.Completed)
              }
            >
              <CheckCircle2 size={12} className="mr-1" />
              Mark complete
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
